import { DataListItem } from '@brighte/form-components';
import { useAuth0 } from '@brighte/passwordless-authenticator';
import { RepaymentType } from '@brighte/validation';
import {
  ErrorResponse,
  PaymentCardType,
  PaymentMethodSource,
  useCreatePaymentMethodMutation,
  useDeletePaymentMethodMutation,
  usePaymentIFrameUrlQuery,
} from '../../graphql';
import { useApplicationData, useSetApplicationData } from '../../provider';
import { KNOWN_ERROR_MESSAGES, LABELS, REPAYMENT_ACCOUNT } from '../../utils';

export function useRepaymentAccountSummary(): Array<{ title: string; items: DataListItem[] | [] }> | [] {
  const paymentDetails = useApplicationData('PaymentDetails');

  if (!paymentDetails?.payment) return [];
  const { BSB, ACCOUNT_NUMBER, CREDIT_CARD_HOLDER, CREDIT_CARD_NUMBER } = LABELS;
  const isCreditCard = paymentDetails?.payment.type === RepaymentType.CreditCard;
  const payment = paymentDetails.payment;

  const repaymentAccountSummary = [
    {
      title: isCreditCard ? (payment?.creditCard?.cardHolder ?? '') : (payment?.directDebit?.accountName ?? ''),
      items: isCreditCard
        ? [
            { label: CREDIT_CARD_HOLDER, value: payment?.creditCard?.cardHolder },
            { label: CREDIT_CARD_NUMBER, value: payment?.creditCard?.cardNumber },
          ]
        : [
            {
              label: BSB,
              value: payment?.directDebit?.bsb,
            },
            {
              label: ACCOUNT_NUMBER,
              value: payment?.directDebit?.accountNumber,
            },
          ],
    },
  ];

  return repaymentAccountSummary;
}

export function useSavePaymentDetails() {
  const { user } = useAuth0();
  const setApplicationData = useSetApplicationData();
  const { mutate: createPaymentMethod, isLoading } = useCreatePaymentMethodMutation();

  const { mutate: deletePaymentMethod } = useDeletePaymentMethodMutation();

  const onSavePaymentDetails = async (
    formValues,
    onSuccess?: (paymentMethodId: string) => void,
    onError?: (error: unknown) => void,
  ) => {
    const payment = formValues.payment;
    const { bsb, ...accountDetails } = payment.directDebit || {};

    const debitParams = {
      paymentMethod: {
        ...accountDetails,
        accountBsb: bsb,
        source: PaymentMethodSource.CustomerWebApplication,
        userId: user?.id,
        type: payment?.type,
      },
    };
    const creditParams = {
      paymentMethod: {
        ...debitParams.paymentMethod,
        ...payment?.creditCard,
        cardType: /mastercard/i.test(payment?.creditCard?.cardType)
          ? PaymentCardType.MASTERCARD
          : payment?.creditCard?.cardType,
      },
    };

    if (payment?.methodId && user) {
      deletePaymentMethod(
        {
          userId: user.id,
          deletePaymentMethodId: payment?.methodId,
        },
        {
          onSuccess: () => {
            setApplicationData({
              PaymentDetails: {
                ...formValues,
                payment: {
                  ...formValues.payment,
                  methodId: undefined,
                },
              },
            });
          },
          onError: errorResponse => onError?.(errorResponse),
        },
      );
    }

    createPaymentMethod(payment?.type === RepaymentType.DirectDebit ? debitParams : creditParams, {
      onSuccess: data => {
        onSuccess?.(data.createPaymentMethod.id);
      },
      onError: errorResponse => {
        const errorMessage = (errorResponse as ErrorResponse)?.response?.errors?.[0].message;
        const message =
          errorMessage === REPAYMENT_ACCOUNT.EXISTING_PAYMENT_METHOD_ERROR
            ? REPAYMENT_ACCOUNT.EXISTING_PAYMENT_METHOD_ERROR_DESCRIPTION
            : KNOWN_ERROR_MESSAGES.DEFAULT;
        // Create a new error object with the updated message
        const updatedErrorResponse = {
          ...(errorResponse as ErrorResponse),
          response: {
            ...(errorResponse as ErrorResponse)?.response,
            errors: [{ message }],
          },
        };

        onError?.(updatedErrorResponse);
      },
    });
  };

  return {
    onSavePaymentDetails,
    isLoading,
  };
}

export function useGetPaymentIFrameUrl({ onSuccess }: { onSuccess: () => void }) {
  const { isFetching, data } = usePaymentIFrameUrlQuery(
    {
      source: PaymentMethodSource.Portal,
    },
    { onSuccess },
  );

  return {
    iframeUrl: data?.paymentIFrameUrl?.url,
    isLoading: isFetching,
  };
}
